import React from 'react'
import { cn } from '@/utils'
import { CheckCircle2, XCircle } from 'lucide-react'

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string
  name: string
  type?: string
  label?: string
  error?: boolean
  success?: boolean
  errorMessage?: string
  successMessage?: string
  className?: string
  rightIcon?: React.ReactNode
  displayIcon?: boolean
  required?: boolean
}

export const TextInput = ({
  id,
  name,
  type = 'text',
  label,
  error,
  success,
  errorMessage,
  successMessage,
  className,
  rightIcon,
  displayIcon = true,
  disabled,
  required,
  ...props
}: Props) => {
  const helperTextColor = error
    ? 'text-red-error'
    : success
    ? 'text-green-100'
    : 'text-gray-500'

  const inputBorderColor = error
    ? 'border-red-error focus:border-red-error'
    : success
    ? 'border-green-100 focus:border-green-100 focus:border-[1.5px]'
    : 'border-gray-300 focus:border-blue-500'

  const defaultIcon = error ? (
    <XCircle className="h-5 w-5 text-red-error" />
  ) : success ? (
    <CheckCircle2 className="h-5 w-5 text-green-100" />
  ) : null

  const showIcon = displayIcon && (rightIcon || (defaultIcon && (error || success)))

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          className={cn(
            'text-sm font-medium text-gray-700',
            disabled && 'text-gray-400 cursor-not-allowed',
          )}
        >
          {label}
          {required && <span className="text-red-error ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          name={name}
          type={type}
          disabled={disabled}
          required={required}
          className={cn(
            'w-full rounded-md border px-3 py-2 text-sm transition-colors',
            'placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-opacity-50',
            'disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200',
            error && 'focus:ring-red-light',
            success && 'focus:ring-green-100',
            !error && !success && 'focus:ring-blue-500',
            inputBorderColor,
            showIcon && 'pr-10',
            className,
          )}
          {...props}
        />
        {showIcon && (
          <div
            className={cn(
              'absolute right-3 top-1/2 -translate-y-1/2',
              disabled && 'opacity-50',
            )}
          >
            {rightIcon || defaultIcon}
          </div>
        )}
      </div>
      {(errorMessage || successMessage) && (
        <span
          className={cn(
            'text-xs',
            helperTextColor,
            disabled && 'text-gray-400',
          )}
        >
          {error ? errorMessage : successMessage}
        </span>
      )}
    </div>
  )
}

TextInput.displayName = 'TextInput'